import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import { mailIntakeDelete, mailIntakeKey, mailIntakeMessagesKey, mailIntakeMessagesQuery, mailIntakeOutboxKey, mailIntakeOutboxQuery, mailIntakeQuery, mailIntakeResend, mailIntakeRetry, mailIntakeSave, mailIntakeTest, type MailIntakePage, type MailIntakeSetting, type MailIntakeTest } from '../../api/settingsMailIntake'
import { Spinner } from '../../ui/Spinner'
import { useMsg } from './AutonomyPage'

/**
 * Sectiunea "Email intake" de sub biroul de service: casuta pe care o
 * citeste platforma pentru cererile catre Adam (docs/plan-adam-prin-email.md,
 * F1). Acelasi scop ca panoul biroului (`dscope`): firma, sau casuta generala
 * a furnizorului pentru un Global Admin. Conectarea, modul (shadow / live),
 * politica de intrare, proba, si ce a venit pe email cu ce s-a hotarat.
 */
const DECISION: Record<string, [string, string]> = {
  ticket_new: ['new ticket', '#2e7d32'],
  ticket_reply: ['reply in ticket', '#1f5f8b'],
  rejected: ['refused', '#b54708'],
  ignored: ['ignored', '#6b7480'],
  error: ['error', '#b42318'],
  pending: ['pending', '#6b7480'],
}

const UNKNOWN: Record<string, string> = {
  reject: 'reply that the address is not known and do nothing else',
  ticket_human: 'open a ticket for a colleague, without the agent',
  ignore: 'ignore the message (moved to the failed folder)',
}

function blank(m365: string | null): Form {
  return {
    enabled: false, mode: 'shadow', kind: m365 ? 'graph' : 'imap', address: '', graph_tenant_id: m365 ?? '', imap_host: '', imap_port: '993', imap_tls: true, imap_user: '', password: '',
    folder_in: 'INBOX', folder_done: 'OD-Done', folder_failed: 'OD-Failed', unknown_senders: 'ticket_human', require_dmarc: true, reply_mode: 'mailbox', max_per_sender_day: '20',
    authserv_id: '',
  }
}

interface Form {
  enabled: boolean; mode: 'shadow' | 'live'; kind: 'graph' | 'imap'; address: string; graph_tenant_id: string; imap_host: string; imap_port: string; imap_tls: boolean; imap_user: string; password: string
  folder_in: string; folder_done: string; folder_failed: string; unknown_senders: string; require_dmarc: boolean; reply_mode: string; max_per_sender_day: string
  authserv_id: string
}

function fromSetting(x: MailIntakeSetting | null, m365: string | null): Form {
  if (!x) return blank(m365)
  return {
    enabled: x.enabled, mode: x.mode === 'live' ? 'live' : 'shadow', kind: x.kind, address: x.address, graph_tenant_id: x.graph_tenant_id ?? m365 ?? '', imap_host: x.imap_host ?? '', imap_port: String(x.imap_port), imap_tls: x.imap_tls, imap_user: x.imap_user ?? '', password: '',
    folder_in: x.folder_in, folder_done: x.folder_done, folder_failed: x.folder_failed, unknown_senders: x.unknown_senders, require_dmarc: x.require_dmarc, reply_mode: x.reply_mode, max_per_sender_day: String(x.max_per_sender_day),
    authserv_id: x.authserv_id ?? '',
  }
}

function trustLine(r: { sender_kind: string; dmarc: string | null; trust_level?: number | null; authserv_ok?: boolean | null; requester_class?: string | null }): string {
  const dmarc = r.dmarc ? `DMARC ${r.dmarc}` : 'no DMARC'
  const auth = r.authserv_ok === true ? 'authserv ok' : r.authserv_ok === false ? 'other host' : 'unknown'
  const trust = r.trust_level === null || r.trust_level === undefined ? null : `trust ${r.trust_level}`
  const bits = [r.sender_kind]
  if (trust !== null) bits.push(`${trust} (${dmarc}, ${auth})`)
  else bits.push(dmarc)
  if (r.requester_class) bits.push(r.requester_class)
  return bits.join(' · ')
}

function fmt(d: string | null): string {
  if (!d) return ''
  const t = new Date(d.replace(' ', 'T') + 'Z')
  if (Number.isNaN(t.getTime())) return d
  return `${String(t.getDate()).padStart(2, '0')} ${t.toLocaleString('en-GB', { month: 'short' })} ${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`
}

export function MailIntakePanel({ scope, companyId, companyName }: { scope: 'company' | 'sp'; companyId: number; companyName: string }) {
  const client = useQueryClient()
  const page = useQuery(mailIntakeQuery(scope))
  const d = page.data
  const [f, setF] = useState<Form | null>(null)
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null)
  const [probe, setProbe] = useState<MailIntakeTest | null>(null)
  const [msg, say] = useMsg()

  useEffect(() => {
    if (d) {
      setF(fromSetting(d.setting, d.m365Tenant))
      setFieldError(null)
    }
  }, [d])

  if (page.error instanceof ApiError && page.error.status === 403) return null
  if (!d || !f) return <div className="panel panel-default" id="odmi"><div className="panel-body"><Spinner /></div></div>

  const set = (patch: Partial<Form>) => setF({ ...f, ...patch })
  const put = (next: MailIntakePage) => client.setQueryData(mailIntakeKey(scope), next)
  const err = (field: string) => (fieldError?.field === field ? fieldError.message : null)
  const cls = (field: string) => 'form-group' + (err(field) ? ' has-error' : '')
  const fail = (e: unknown) => {
    if (e instanceof ApiError && e.details && typeof e.details === 'object' && 'field' in e.details) {
      const det = e.details as { field?: string; message?: string }
      setFieldError({ field: String(det.field ?? ''), message: det.message ?? e.message })
    }
    say(e instanceof ApiError && e.message ? e.message : 'Failed', 'bad')
  }

  const save = () => {
    setFieldError(null)
    say('...', 'wait')
    void mailIntakeSave({
      scope, enabled: f.enabled ? '1' : '0', mode: f.mode, kind: f.kind, address: f.address, graph_tenant_id: f.graph_tenant_id, imap_host: f.imap_host, imap_port: f.imap_port, imap_tls: f.imap_tls ? '1' : '0',
      imap_user: f.imap_user, password: f.password, folder_in: f.folder_in, folder_done: f.folder_done, folder_failed: f.folder_failed, unknown_senders: f.unknown_senders,
      require_dmarc: f.require_dmarc ? '1' : '0', reply_mode: f.reply_mode, max_per_sender_day: f.max_per_sender_day,
      authserv_id: f.authserv_id,
    })
      .then((next) => { put(next); setProbe(null); say('Saved', 'ok'); void client.invalidateQueries({ queryKey: mailIntakeMessagesKey(scope) }) })
      .catch(fail)
  }
  const test = () => {
    say('testing...', 'wait')
    void mailIntakeTest(scope)
      .then((r) => { setProbe(r); say(r.ok ? 'Mailbox answered' : r.error ?? 'Failed', r.ok ? 'ok' : 'bad'); void client.invalidateQueries({ queryKey: mailIntakeKey(scope) }) })
      .catch(fail)
  }
  const remove = () => {
    say('...', 'wait')
    void mailIntakeDelete(scope).then((next) => { put(next); setProbe(null); say('Removed', 'ok') }).catch(fail)
  }

  const x = d.setting
  const who = scope === 'sp' ? 'the provider mailbox (all companies)' : companyName || `#${companyId}`

  return (
    <div className="panel panel-default" id="odmi" data-scope={scope}>
      <div className="panel-body">
        <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>Email intake</strong>
          <span className="text-muted" style={{ fontSize: 12 }}>
            a mailbox the platform reads every minute: a request sent there by a known person of the company becomes a ticket for the service desk above, the agent handles it within what the desk policy allows and answers back by email, in the same thread. Per company, next to the provider&apos;s general mailbox; off until you turn it on. Reading starts from the moment it is switched on - older mail in the folder is left alone.
          </span>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginTop: 10, fontSize: 12 }}>
          <span>Editing: <strong>{who}</strong></span>
          {x ? <span style={{ color: x.enabled ? '#2e7d32' : '#6b7480' }}>{x.enabled ? (x.mode === 'live' ? 'on, live' : 'on, shadow') : 'saved, off'}</span> : <span className="text-muted">not set</span>}
          {x?.last_poll_at && (
            <span style={{ color: x.last_error ? '#b42318' : '#6b7480' }}>
              last read {fmt(x.last_poll_at)}{x.last_error ? `: ${x.last_error}` : ''}{x.fail_count > 1 ? ` (${x.fail_count} times in a row)` : ''}
            </span>
          )}
          {x?.checked_at && (
            <span style={{ color: x.check_ok ? '#2e7d32' : '#b42318' }}>
              last probe {fmt(x.checked_at)}: {x.check_ok ? 'ok' : x.check_error || 'failed'}
            </span>
          )}
        </div>
        {!d.ready && <div style={{ fontSize: 12, marginTop: 8, color: '#b42318' }}>The email intake tables are missing: the database migrations must be run first. The form is shown, but nothing can be saved yet.</div>}
        {!d.cryptoReady && <div style={{ fontSize: 12, marginTop: 8, color: '#b54708' }}>Encryption is not configured on this server: IMAP passwords cannot be stored.</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '12px 24px', marginTop: 12, fontSize: 12 }}>
          <div>
            <div className={cls('enabled')} style={{ margin: 0 }}>
              <label style={{ display: 'block' }}><input type="checkbox" id="odmi-enabled" checked={f.enabled} onChange={(e) => set({ enabled: e.target.checked })} /> <strong>Read this mailbox</strong></label>
              <span className="help-block" id="odmi-enabled-help" style={{ marginTop: 0 }}>{err('enabled') ?? (x?.enabled ? 'To change the connection, switch it off, save, run Test, then switch it on again.' : 'Save the connection first, run Test, then switch it on: it starts only after a passing Test.')}</span>
            </div>
            <div style={{ marginTop: 6 }}>
              <label style={{ display: 'block' }}><input type="radio" name="odmi-mode" value="shadow" checked={f.mode === 'shadow'} onChange={() => set({ mode: 'shadow' })} /> <strong>shadow</strong> - only writes down what it would do; no ticket, no reply, nothing moved. Start here.</label>
              <label style={{ display: 'block', marginTop: 2 }}><input type="radio" name="odmi-mode" value="live" checked={f.mode === 'live'} onChange={() => set({ mode: 'live' })} /> <strong>live</strong> - opens tickets, adds replies, answers back, moves handled mail.</label>
            </div>
            <div className={cls('address')} style={{ marginTop: 8 }}>
              <label htmlFor="odmi-address">Mailbox address</label>
              <input id="odmi-address" className="form-control input-sm" type="email" placeholder="suport@firma.ro" value={f.address} onChange={(e) => set({ address: e.target.value })} />
              {err('address') && <span className="help-block">{err('address')}</span>}
            </div>
            <strong>How it is read</strong>
            <label style={{ display: 'block', marginTop: 4 }}>
              <input type="radio" name="odmi-kind" value="graph" checked={f.kind === 'graph'} onChange={() => set({ kind: 'graph' })} /> <strong>Microsoft 365</strong> (Graph){!d.graphConfigured && <span className="text-muted"> - not configured on this server</span>}
            </label>
            {f.kind === 'graph' && (
              <div className={cls('graph_tenant_id')} style={{ marginLeft: 18 }}>
                <label htmlFor="odmi-tenant">Tenant id</label>
                <input id="odmi-tenant" className="form-control input-sm" value={f.graph_tenant_id} placeholder="00000000-0000-0000-0000-000000000000" onChange={(e) => set({ graph_tenant_id: e.target.value })} />
                <span className="help-block">
                  {err('graph_tenant_id') ?? (d.m365Tenant ? 'Taken from Securitee → Microsoft 365. ' : '')}
                  The tenant must consent to <code>Mail.ReadWrite</code> and <code>Mail.Send</code> for the platform application (Securitee → Microsoft 365 → Connect), and its Exchange admin should restrict the application to this mailbox with an application access policy; the probe checks that.
                </span>
              </div>
            )}
            <label style={{ display: 'block', marginTop: 4 }}>
              <input type="radio" name="odmi-kind" value="imap" checked={f.kind === 'imap'} onChange={() => set({ kind: 'imap' })} /> <strong>IMAP</strong>{!d.imapAvailable && <span className="text-muted"> - not available on this server</span>}
            </label>
            {f.kind === 'imap' && (
              <div style={{ marginLeft: 18 }}>
                <div className={cls('imap_host')}>
                  <label htmlFor="odmi-host">Server</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input id="odmi-host" className="form-control input-sm" placeholder="outlook.office365.com" value={f.imap_host} onChange={(e) => set({ imap_host: e.target.value })} />
                    <input id="odmi-port" className="form-control input-sm" style={{ width: 80 }} type="number" min={1} max={65535} value={f.imap_port} onChange={(e) => set({ imap_port: e.target.value })} />
                  </div>
                  {(err('imap_host') ?? err('imap_port')) && <span className="help-block">{err('imap_host') ?? err('imap_port')}</span>}
                </div>
                <label style={{ display: 'block' }}><input type="checkbox" id="odmi-tls" checked={f.imap_tls} onChange={(e) => set({ imap_tls: e.target.checked })} /> TLS (certificate verified)</label>
                <div className={cls('imap_user')} style={{ marginTop: 6 }}>
                  <label htmlFor="odmi-user">User</label>
                  <input id="odmi-user" className="form-control input-sm" value={f.imap_user} placeholder={f.address || 'usually the mailbox address'} onChange={(e) => set({ imap_user: e.target.value })} />
                  {err('imap_user') && <span className="help-block">{err('imap_user')}</span>}
                </div>
                <div className={cls('password')}>
                  <label htmlFor="odmi-password">Password</label>
                  <input id="odmi-password" className="form-control input-sm" type="password" autoComplete="new-password" value={f.password} placeholder={x?.password_set ? 'Saved. Leave empty to keep it.' : ''} onChange={(e) => set({ password: e.target.value })} />
                  <span className="help-block">{err('password') ?? 'Stored encrypted; it is never shown again.'}</span>
                </div>
              </div>
            )}
          </div>
          <div>
            <strong>Folders</strong>
            <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
              {(['folder_in', 'folder_done', 'folder_failed'] as const).map((k) => (
                <div key={k} className={cls(k)} style={{ margin: 0 }}>
                  <label htmlFor={`odmi-${k}`} style={{ fontWeight: 'normal' }}>{k === 'folder_in' ? 'read from' : k === 'folder_done' ? 'move handled to' : 'move failed to'}</label>
                  <input id={`odmi-${k}`} className="form-control input-sm" style={{ width: 120 }} value={f[k]} onChange={(e) => set({ [k]: e.target.value } as Partial<Form>)} />
                  {err(k) && <span className="help-block">{err(k)}</span>}
                </div>
              ))}
            </div>
            <div className="text-muted" style={{ marginTop: 4 }}>Handled messages are moved, never deleted.</div>

            <strong style={{ display: 'block', marginTop: 10 }}>Who may ask</strong>
            <div className="text-muted">A known person of the company (a contact or an operator) whose message passes DMARC can ask for what the service desk policy above allows. Everything else:</div>
            <div className={cls('unknown_senders')} style={{ marginTop: 4 }}>
              <label htmlFor="odmi-unknown" style={{ fontWeight: 'normal' }}>unknown senders:</label>{' '}
              <select id="odmi-unknown" className="form-control input-sm" style={{ display: 'inline-block', width: 'auto' }} value={f.unknown_senders} onChange={(e) => set({ unknown_senders: e.target.value })}>
                {d.unknownSenders.map((u) => <option key={u} value={u}>{UNKNOWN[u] ?? u}</option>)}
              </select>
            </div>
            <label style={{ display: 'block', marginTop: 4 }}><input type="checkbox" id="odmi-dmarc" checked={f.require_dmarc} onChange={(e) => set({ require_dmarc: e.target.checked })} /> without a DMARC pass the agent only reads and answers; nothing runs on a machine</label>
            <div className={cls('authserv_id')} style={{ marginTop: 8 }}>
              <label htmlFor="odmi-authserv">Authentication-Results host (authserv-id)</label>
              <input id="odmi-authserv" className="form-control input-sm" style={{ maxWidth: 320 }} value={f.authserv_id} placeholder="learned by Test from the newest message" onChange={(e) => set({ authserv_id: e.target.value })} />
              <span className="help-block" style={{ marginTop: 0 }}>
                {err('authserv_id') ?? (f.authserv_id.trim() === ''
                  ? 'not set - the agent only reads; Test learns it from the mailbox host'
                  : 'the agent trusts dmarc=pass only from this host')}
              </span>
            </div>
            <div style={{ marginTop: 6 }}>
              at most <input type="number" min={0} max={1000} id="odmi-max" className="form-control input-sm" style={{ width: 72, display: 'inline-block' }} value={f.max_per_sender_day} onChange={(e) => set({ max_per_sender_day: e.target.value })} /> messages a day from one sender <span className="text-muted">(0 = no cap)</span>
            </div>
            <div style={{ marginTop: 6 }}>
              replies are sent{' '}
              <select id="odmi-reply" className="form-control input-sm" style={{ display: 'inline-block', width: 'auto' }} value={f.reply_mode} onChange={(e) => set({ reply_mode: e.target.value })}>
                <option value="mailbox">from this mailbox, in the same thread</option>
                <option value="noreply">from the provider&apos;s no-reply address</option>
              </select>
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-primary btn-sm" id="odmi-save" onClick={save} disabled={!d.ready}>Save</button>
              {x && <button type="button" className="btn btn-default btn-sm" id="odmi-test" onClick={test}>Test</button>}
              {x && <button type="button" className="btn btn-default btn-sm" id="odmi-delete" title="remove this mailbox" onClick={remove}>Remove</button>}
              {msg}
            </div>
            {probe && (
              <div id="odmi-probe" style={{ marginTop: 8, fontSize: 12 }}>
                {probe.authserv_seen != null && probe.authserv_seen !== '' && (
                  <div style={{ color: probe.authserv_match === false ? '#b42318' : undefined }}>
                    seen: {probe.authserv_seen}{probe.authserv_match === false ? ' (does not match the saved host)' : ''}
                  </div>
                )}
                {probe.ok ? (
                  <>
                    <div style={{ color: '#2e7d32' }}>Connected. Folders: {probe.folders.join(', ') || 'none listed'}.</div>
                    {probe.missing_folders.length > 0 && <div style={{ color: '#b54708' }}>Missing folders: {probe.missing_folders.join(', ')}. Create them in the mailbox before switching on.</div>}
                    {probe.scope_ok === true && <div style={{ color: '#2e7d32' }}>The application is restricted to this mailbox: another mailbox of the tenant was refused.</div>}
                    {probe.scope_ok === false && <div style={{ color: '#b42318' }}>The application can read OTHER mailboxes of this tenant. Ask the Exchange admin for an application access policy limited to this mailbox before switching on.</div>}
                  </>
                ) : (
                  <div style={{ color: '#b42318' }}>{probe.error}</div>
                )}
              </div>
            )}
          </div>
        </div>
        {x && <MailIntakeMessages scope={scope} />}
        {x && <MailIntakeOutbox scope={scope} />}
      </div>
    </div>
  )
}

const OUTBOX_TONE: Record<string, string> = { queued: '#1f5f8b', sent: '#2e7d32', failed: '#b42318' }

/** Raspunsurile din outbox - fila Replies cu Resend pe failed. */
function MailIntakeOutbox({ scope }: { scope: 'company' | 'sp' }) {
  const client = useQueryClient()
  const q = useQuery(mailIntakeOutboxQuery(scope))
  const rows = q.data?.rows ?? []
  const [busy, setBusy] = useState(0)
  const [note, setNote] = useState<{ id: number; ok: boolean; text: string } | null>(null)

  const resend = (id: number) => {
    setBusy(id)
    setNote(null)
    void mailIntakeResend(scope, id)
      .then((r) => {
        setNote({ id, ok: r.ok, text: r.ok ? 'queued again' : r.error ?? 'Not resent' })
        void client.invalidateQueries({ queryKey: mailIntakeOutboxKey(scope) })
      })
      .catch((e: unknown) => setNote({ id, ok: false, text: e instanceof ApiError && e.message ? e.message : 'Not resent' }))
      .finally(() => setBusy(0))
  }

  return (
    <div id="odmi-outbox" style={{ marginTop: 14 }}>
      <strong style={{ fontSize: 13 }}>Replies</strong>
      {rows.length === 0 ? (
        <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>No replies yet. Answers the platform sends appear here with their state.</div>
      ) : (
        <table className="table table-condensed" style={{ fontSize: 12, marginTop: 6 }}>
          <thead><tr><th style={{ width: 110 }}>Created</th><th style={{ width: 180 }}>To</th><th style={{ width: 70 }}>Kind</th><th style={{ width: 70 }}>Ticket</th><th style={{ width: 80 }}>State</th><th>Error</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{fmt(r.created_at)}</td>
                <td>{r.to}<div className="text-muted" style={{ fontSize: 11 }}>{r.subject}</div></td>
                <td><span>{r.kind || 'reply'}</span>{r.lang ? <div className="text-muted" style={{ fontSize: 11 }}>{r.lang}</div> : null}</td>
                <td>{r.ticket_id > 0 ? <a href={`/app/easeedesk/ticket/${r.ticket_id}`}>#{r.ticket_id}</a> : '—'}</td>
                <td>
                  <span style={{ fontWeight: 600, color: OUTBOX_TONE[r.state] ?? '#6b7480' }}>{r.state}</span>
                  <div className="text-muted" style={{ fontSize: 11 }}>attempt {r.attempts}{r.message_id_set ? ' · Message-ID' : ''}</div>
                </td>
                <td style={{ color: '#444' }}>
                  {r.error || (r.sent_at ? `sent ${fmt(r.sent_at)}` : '')}
                  {r.state === 'failed' && (
                    <div style={{ marginTop: 4 }}>
                      <button type="button" className="btn btn-default btn-xs odmi-resend" disabled={busy === r.id} onClick={() => resend(r.id)}>Resend</button>
                      {note?.id === r.id && <span style={{ marginLeft: 6, color: note.ok ? '#2e7d32' : '#b42318' }}>{note.text}</span>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/** Ce a venit pe email si ce s-a hotarat - `it_mail_intake_messages`, cele mai noi intai. */
function MailIntakeMessages({ scope }: { scope: 'company' | 'sp' }) {
  const client = useQueryClient()
  const q = useQuery(mailIntakeMessagesQuery(scope))
  const rows = q.data?.rows ?? []
  const [busy, setBusy] = useState(0)
  const [note, setNote] = useState<{ id: number; ok: boolean; text: string } | null>(null)

  /* Mesajul se citeste din nou din casuta (dupa id-ul lui Microsoft 365) si trece prin toti pasii. */
  const retry = (id: number) => {
    setBusy(id)
    setNote(null)
    void mailIntakeRetry(scope, id)
      .then((r) => {
        setNote({ id, ok: r.ok, text: r.ok ? `done: ${DECISION[r.decision ?? '']?.[0] ?? r.decision ?? ''}` : r.error ?? 'Not retried' })
        void client.invalidateQueries({ queryKey: mailIntakeMessagesKey(scope) })
      })
      .catch((e: unknown) => setNote({ id, ok: false, text: e instanceof ApiError && e.message ? e.message : 'Not retried' }))
      .finally(() => setBusy(0))
  }

  return (
    <div id="odmi-messages" style={{ marginTop: 14 }}>
      <strong style={{ fontSize: 13 }}>What came in</strong>
      {rows.length === 0 ? (
        <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>Nothing yet. Each message read appears here with what was decided and why.</div>
      ) : (
        <table className="table table-condensed" style={{ fontSize: 12, marginTop: 6 }}>
          <thead><tr><th style={{ width: 110 }}>Received</th><th style={{ width: 200 }}>From</th><th>Subject</th><th style={{ width: 56 }}>Lang</th><th style={{ width: 56 }}>Ack</th><th style={{ width: 120 }}>Decision</th><th>Why</th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const [label, tone] = DECISION[r.decision] ?? [r.decision, '#6b7480']
              return (
                <tr key={r.id}>
                  <td>{fmt(r.received_at)}</td>
                  <td>
                    {r.from}
                    <div className="text-muted" style={{ fontSize: 11 }}>{trustLine(r)}</div>
                  </td>
                  <td>{r.subject}</td>
                  <td className="text-muted">{r.lang ?? '—'}</td>
                  <td className="text-muted">{r.acked_at ? 'yes' : '—'}</td>
                  <td>
                    <span style={{ fontWeight: 600, color: tone }}>{label}</span>
                    {r.shadow && <div className="text-muted" style={{ fontSize: 11 }}>shadow</div>}
                    {r.ticket_id > 0 && !r.shadow && <div><a href={`/app/easeedesk/ticket/${r.ticket_id}`}>#{r.ticket_id}</a></div>}
                    {(r.attachments ?? 0) > 0 && <div className="text-muted odmi-files" style={{ fontSize: 11 }}>{r.attachments} attachment{r.attachments === 1 ? '' : 's'}</div>}
                  </td>
                  <td style={{ color: '#444' }}>
                    {r.reason}
                    {r.retry && (
                      <div style={{ marginTop: 4 }}>
                        <button type="button" className="btn btn-default btn-xs odmi-retry" disabled={busy === r.id} onClick={() => retry(r.id)}>Retry</button>
                        {note?.id === r.id && <span style={{ marginLeft: 6, color: note.ok ? '#2e7d32' : '#b42318' }}>{note.text}</span>}
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
